import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "lcs_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  localStorage.setItem(TOKEN_KEY, t);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// ── Auth ──
export const requestMagicLink = (email) =>
  api.post("/auth/request-link", { email }).then((r) => r.data);

export const verifyToken = (token) =>
  api.post("/auth/verify", { token }).then((r) => r.data);

export const fetchMe = () => api.get("/me").then((r) => r.data);

// ── Account (read-only) ──
// Full pre-deletion export. Read-only GET — never mutates, never deletes.
export const exportAccount = () => api.get("/account/export").then((r) => r.data);
// Read-only deletion preflight/inventory. Never mutates; no prepare/confirm/DCC/fence.
export const getDeletionPreflight = () =>
  api.get("/account/deletion/preflight").then((r) => r.data);

// ── Account deletion (staged, gated). All identity is server-derived; no client identity is sent. ──
// Step A: mint a deletion attempt (JWT-authenticated). Returns generation + one-time control secrets.
export const prepareAccountDeletion = () =>
  api.post("/account/deletion/prepare").then((r) => r.data);
// Step B: create the soft-lock (JWT-authenticated). Body carries only the server-issued generation + dcc.
export const confirmAccountDeletion = (generation, dcc) =>
  api.post("/account/deletion/confirm",
    { deletion_request_generation: generation, dcc }).then((r) => r.data);
// Post-lock control surface — authorized ONLY by the one-time control credential (never the JWT).
const dccHeader = (dcc) => ({ headers: { "X-Deletion-Control-DCC": dcc } });
export const getDeletionControlStatus = (dcc) =>
  api.get("/deletion-control/status", dccHeader(dcc)).then((r) => r.data);
export const getConfirmationEnvelope = (dcc) =>
  api.get("/deletion-control/confirmation-envelope", dccHeader(dcc)).then((r) => r.data);
export const advanceDeletionCapture = (dcc) =>
  api.post("/deletion-control/advance-capture", { dcc }).then((r) => r.data);
export const cancelAccountDeletion = (dcc) =>
  api.post("/deletion-control/cancel", { dcc }).then((r) => r.data);
export const confirmDestructiveDeletion = ({ dcc, expected_rev, snapshot_hash }) =>
  api.post("/deletion-control/confirm-destructive-start/minimized",
    { dcc, expected_rev, snapshot_hash }).then((r) => r.data);

// ── Data ──
export const saveProfile = (profile) =>
  api.put("/profile", profile).then((r) => r.data);

export const toggleCompleted = (day, done) =>
  api.post("/completed", { day, done }).then((r) => r.data);

export const generatePost = (payload) =>
  api.post("/generate-post", payload).then((r) => r.data);

export const getGeneratePostJob = (jobId) =>
  api.get(`/generate-post/job/${jobId}`).then((r) => r.data);

export const addVaultItem = (item) =>
  api.post("/vault", item).then((r) => r.data);

export const updateVaultItem = (id, patch) =>
  api.put(`/vault/${id}`, patch).then((r) => r.data);

export const deleteVaultItem = (id) =>
  api.delete(`/vault/${id}`).then((r) => r.data);

// ── Test Your Findability ──
export const getFindability = () =>
  api.get("/findability-check").then((r) => r.data);

export const generateFindability = () =>
  api.post("/findability-check/generate").then((r) => r.data);

export const regenerateFindability = () =>
  api.post("/findability-check/regenerate").then((r) => r.data);

export const getFindabilityJob = (jobId) =>
  api.get(`/findability-check/job/${jobId}`).then((r) => r.data);

export const saveFindabilityNotes = (payload) =>
  api.put("/findability-check/notes", payload).then((r) => r.data);

// ── Findability Cycle lifecycle (paid entry / activation handoff) ──
export const getLifecycle = () =>
  api.get("/cycle/lifecycle").then((r) => r.data);

export const activateCycle = (focus) =>
  api.post("/cycle/activate", focus).then((r) => r.data);

// Multi-cycle switcher: make an owned active cycle the current/landing one (flag ON).
export const selectCycle = (cycleId) =>
  api.post("/cycle/select", { cycle_id: cycleId }).then((r) => r.data);

// Optional multi-cycle scoping. When cycleId is falsy the param/field is omitted, so single-cycle
// and flag-OFF behavior is byte-for-byte unchanged.
const cq = (cycleId) => (cycleId ? { params: { cycle_id: cycleId } } : undefined);
const withCycle = (body, cycleId) => (cycleId ? { ...body, cycle_id: cycleId } : body);

// ── Findability Cycle: Baseline (Day 0) draft + explicit confirm ──
// The current pre-Baseline cycle is resolved server-side from the JWT; cycle_id is sent only to
// disambiguate when the account has multiple active cycles (multi-cycle flag ON).
export const saveBaselineDraft = (payload, cycleId) =>
  api.put("/cycle/baseline", withCycle(payload, cycleId)).then((r) => r.data);

export const confirmBaseline = (cycleId) =>
  api.post("/cycle/baseline/confirm", null, cq(cycleId)).then((r) => r.data);

// Cycle-bound Baseline phrase generation — derived server-side from the cycle's activated_focus
// (no title/audience is ever sent). Regenerate = call generate again.
export const generateCyclePhrases = (cycleId) =>
  api.post("/cycle/baseline/phrases", null, cq(cycleId)).then((r) => r.data);

export const getCyclePhraseJob = (jobId) =>
  api.get(`/cycle/baseline/phrases/job/${jobId}`).then((r) => r.data);

// Read-only recovery of the current cycle's Baseline draft (phrases/source/note/answers/confirmed).
export const getBaselineDraft = (cycleId) =>
  api.get("/cycle/baseline", cq(cycleId)).then((r) => r.data);

// ── Findability Cycle: branded-Challenge cycle-backed data (CB-4B) ──
export const getCycleEfforts = (cycleId) =>
  api.get("/cycle/efforts", cq(cycleId)).then((r) => r.data);
export const mutateCycleEffort = (day, completed, cycleId) =>
  api.post("/cycle/effort", withCycle({ day, completed }, cycleId)).then((r) => r.data);
export const listCycleVault = (cycleId) =>
  api.get("/cycle/vault", cq(cycleId)).then((r) => r.data);
export const createCycleVault = (item, cycleId) =>
  api.post("/cycle/vault", item, cq(cycleId)).then((r) => r.data);
export const editCycleVault = (id, patch) =>
  api.put(`/cycle/vault/${id}`, patch).then((r) => r.data);
export const deleteCycleVault = (id) =>
  api.delete(`/cycle/vault/${id}`).then((r) => r.data);

// Day 30 Progress Review + explicit Final Completion (drives the existing canonical lifecycle).
export const saveProgressReview = ({ note, answers }, cycleId) =>
  api.put("/cycle/progress-review", withCycle({ note, answers }, cycleId)).then((r) => r.data);
export const confirmProgressReview = (cycleId) =>
  api.post("/cycle/progress-review/confirm", null, cq(cycleId)).then((r) => r.data);
export const completeCycle = (completionActionToken) =>
  api.post("/cycle/complete", { completion_action_token: completionActionToken }).then((r) => r.data);

// ── Past Cycles (read-only history/archive of terminal cycles + their cycle-bound saved posts) ──
export const getCycleHistory = () =>
  api.get("/cycle/history").then((r) => r.data);
export const getCycleHistoryRecord = (cycleId) =>
  api.get(`/cycle/history/${cycleId}`).then((r) => r.data);
